import { useEffect, useRef, useState } from 'react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';
import { createPortal } from 'react-dom';

/**
 * Animates a React ref element from scale 0 → 1 and opacity 0 → 1
 * on scroll enter. Pass the ref directly — no DOM id lookup needed.
 *
 * @param {React.RefObject} ref  Ref attached to the element to animate
 * @param {Object} [options]
 * @param {number} [options.duration=0.8]
 * @param {number} [options.delay=0.4]   Delay after trigger fires (seconds)
 * @param {string} [options.ease='power2.out']
 * @param {string} [options.triggerStart='top 85%']
 * @param {boolean} [options.once=true]
 * @param {boolean} [options.markers=false]
 */
export const useScaleReveal = (ref, options = {}) => {
  const {
    duration = 0.8,
    delay = 0.4,
    ease = 'power2.out',
    triggerStart = 'top 85%',
    once = true,
    markers = false,
    scrub = false,
  } = options;

  useEffect(() => {
    const el = ref?.current;
    if (!el || typeof window === 'undefined') return;

    gsap.set(el, { scale: 0.8, opacity: 0, transformOrigin: '50% 50%' });

    const tween = gsap.to(el, {
      scale: 1,
      opacity: 1,
      duration,
      delay,
      ease,
      scrollTrigger: {
        trigger: el,
        start: triggerStart,
        once,
        markers,
        scrub
      },

    });

    return () => {
      tween?.scrollTrigger?.kill();
      tween?.kill();
    };
  }, []);
};


if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

let pendingRefreshFrame = null;
const batchScrollTriggerRefresh = () => {
  if (typeof window === 'undefined') return;
  if (pendingRefreshFrame) return;
  pendingRefreshFrame = requestAnimationFrame(() => {
    pendingRefreshFrame = null;
    ScrollTrigger.refresh();
  });
};

/**
 * Scroll-triggered reveal animation for an element looked up by DOM id.
 *
 * @param {string|null} id Element id from Sharetribe Console (pass null to skip)
 * @param {Object} [options]
 * @param {number} [options.fromYPercent=60]
 * @param {number} [options.fromOpacity=0]
 * @param {number} [options.duration=1.4]
 * @param {number} [options.delay=0]
 * @param {boolean} [options.markers=false]
 * @param {boolean} [options.once=true]
 * @param {boolean} [options.scrub=false]
 * @param {string} [options.triggerStart='top 85%']
 * @param {string} [options.triggerEnd='top 60%']
 */
/**
 * Scroll-triggered scale reveal animation (scale 0 → 1) for a single element by DOM id.
 * Only intended for #centerimg — do not reuse for other elements.
 *
 * @param {string|null} id  Element id to animate (pass null to skip)
 * @param {Object} [options]
 * @param {number} [options.duration=1.6]
 * @param {number} [options.delay=0]
 * @param {string} [options.ease='power3.out']
 * @param {boolean} [options.once=true]
 * @param {string} [options.triggerStart='top 80%']
 * @param {boolean} [options.markers=false]
 */
export const useScaleAnimate = (id, options = {}) => {
  const {
    duration = 1.6,
    delay = 0,
    ease = 'power3.out',
    once = true,
    triggerStart = 'top 80%',
    markers = false,
  } = options;

  useEffect(() => {
    if (!id || typeof window === 'undefined') return;

    let tween = null;
    let cancelled = false;
    let observer = null;
    let refreshTimeout = null;

    const refresh = () => {
      if (!cancelled) batchScrollTriggerRefresh();
    };

    const setup = (element) => {
      gsap.killTweensOf(element);
      ScrollTrigger.getAll().forEach(st => {
        if (st.trigger === element) st.kill();
      });

      gsap.set(element, { scale: 0.6, opacity: 0, transformOrigin: '50% 50%' });

      tween = gsap.to(element, {
        scale: 1,
        opacity: 1,
        duration,
        ease,
        delay,
        scrollTrigger: {
          trigger: element,
          start: triggerStart,
          once,
          markers,
        },
      });

      refresh();
    };

    const trySetup = () => {
      const element = document.getElementById(id);
      if (element) { setup(element); return true; }
      return false;
    };

    if (!trySetup()) {
      observer = new MutationObserver(() => {
        if (cancelled) return;
        if (trySetup()) { observer.disconnect(); observer = null; }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    const handleLoad = () => refresh();
    window.addEventListener('load', handleLoad);

    window.addEventListener('pageshow', handleLoad);

    refreshTimeout = setTimeout(refresh, 200);

    return () => {
      cancelled = true;
      observer?.disconnect();
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('pageshow', handleLoad);
      if (refreshTimeout) clearTimeout(refreshTimeout);
      tween?.scrollTrigger?.kill();
      tween?.kill();
    };
  }, [id, duration, delay, ease, once, triggerStart, markers]);
};

export const useCustomIdAnimate = (id, options = {}) => {
  const {
    fromYPercent = 60,
    fromOpacity = 0,
    duration = 3.4,
    delay = 0,
    markers = false,
    once = false,
    scrub = true,
    triggerStart = 'top 90%',
    triggerEnd = 'top 36%',
  } = options;

  useEffect(() => {
    if (!id || typeof window === 'undefined') return;

    let tween = null;
    let cancelled = false;
    let refreshTimeout = null;

    const setup = () => {
      const element = document.getElementById(id);
      if (!element) return false;

      gsap.killTweensOf(element);
      ScrollTrigger.getAll().forEach(st => {
        if (st.trigger === element) st.kill();
      });

      gsap.set(element, {
        yPercent: fromYPercent,
        opacity: fromOpacity,
      });

      tween = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: triggerStart,
          end: triggerEnd,
          markers,
          once,
          scrub,
        },
      });

      tween.to(element, {
        yPercent: 0,
        opacity: 1,
        duration,
        ease: 'power3.out',
        delay,
      });

      batchScrollTriggerRefresh();
      return true;
    };

    if (!setup()) {
      refreshTimeout = window.setTimeout(() => {
        if (!cancelled) setup();
      }, 0);
    }

    const handleLoad = () => batchScrollTriggerRefresh();
    window.addEventListener('load', handleLoad);

    return () => {
      cancelled = true;
      if (refreshTimeout) window.clearTimeout(refreshTimeout);
      window.removeEventListener('load', handleLoad);
      tween?.scrollTrigger?.kill();
      tween?.kill();
    };
  }, [id, fromYPercent, fromOpacity, duration, delay, markers, once, scrub, triggerStart, triggerEnd]);
};


/**
 * SectionCursor
 *
 * Renders a custom cursor that follows the mouse inside a section identified
 * by `sectionId`. Two visual variants are available:
 *
 * - "cta"    (default) — small black circle, `ctaCustomCursor` class.
 *                        Used for #centerimg and similar CTA sections.
 * - "scroll"           — larger frosted-glass circle, `customCursor` class,
 *                        matching the ScrollDown hero cursor aesthetic.
 *                        Uses GSAP for smooth enter/leave transitions.
 *                        Used for video-container sections.
 *
 * @param {string}  sectionId  DOM id of the section to attach listeners to
 * @param {string}  [label]    Text rendered inside the cursor bubble
 * @param {"cta"|"scroll"} [variant="cta"]  Visual style of the cursor
 */
export default function SectionCursor({ sectionId, label, variant = 'cta' }) {
  const cursorRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const section = document.getElementById(sectionId);
    const cursor = cursorRef.current;
    if (!section || !cursor) return;

    if (variant === 'scroll') {
      // ── GSAP variant (matches ScrollDown.js hero cursor) ──────────────────
      gsap.set(cursor, {
        xPercent: -50,
        yPercent: -50,
        opacity: 0,
        scale: 0.8,
      });

      const handleMove = (e) => {
        gsap.to(cursor, {
          x: e.clientX,
          y: e.clientY,
          duration: 0.35,
          ease: 'power3.out',
          overwrite: 'auto',
        });
      };

      const handleEnter = (e) => {
        gsap.killTweensOf(cursor);
        gsap.to(cursor, {
          x: e.clientX,
          y: e.clientY,
          opacity: 1,
          scale: 1,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: 'auto',
        });
        section.style.cursor = 'none';
      };

      const handleLeave = () => {
        gsap.killTweensOf(cursor);
        gsap.to(cursor, {
          opacity: 0,
          scale: 0.8,
          duration: 0.2,
          ease: 'power2.out',
        });
        section.style.cursor = 'auto';
      };

      section.addEventListener('mousemove', handleMove);
      section.addEventListener('mouseenter', handleEnter);
      section.addEventListener('mouseleave', handleLeave);

      return () => {
        gsap.killTweensOf(cursor);
        section.removeEventListener('mousemove', handleMove);
        section.removeEventListener('mouseenter', handleEnter);
        section.removeEventListener('mouseleave', handleLeave);
        section.style.cursor = 'auto';
      };
    }

    // ── Default lerp variant (ctaCustomCursor) ────────────────────────────
    let mouseX = 0;
    let mouseY = 0;
    let currentX = 0;
    let currentY = 0;
    let raf;

    const lerp = (start, end, factor) => start + (end - start) * factor;

    const animate = () => {
      currentX = lerp(currentX, mouseX, 0.18);
      currentY = lerp(currentY, mouseY, 0.18);
      cursor.style.transform = `translate(${currentX}px, ${currentY}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(animate);
    };

    const handleMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const handleEnter = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      currentX = mouseX;
      currentY = mouseY;
      cursor.style.opacity = 1;
      section.style.cursor = 'none';
    };

    const handleLeave = () => {
      cursor.style.opacity = 0;
      section.style.cursor = 'auto';
    };

    section.addEventListener('mousemove', handleMove);
    section.addEventListener('mouseenter', handleEnter);
    section.addEventListener('mouseleave', handleLeave);

    animate();

    return () => {
      cancelAnimationFrame(raf);
      section.removeEventListener('mousemove', handleMove);
      section.removeEventListener('mouseenter', handleEnter);
      section.removeEventListener('mouseleave', handleLeave);
      section.style.cursor = 'auto';
    };
  }, [sectionId, variant, mounted]);

  if (!mounted) return null;

  return createPortal(
    <div
      ref={cursorRef}
      className={variant === 'scroll' ? 'customCursor' : 'ctaCustomCursor'}
      style={variant === 'scroll' ? {
        position: 'fixed',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: 9999,
        willChange: 'transform, opacity',
      } : undefined}
    >
      {label}
    </div>,
    document.body
  );
}